import type { PropsOf } from '@emotion/react';
import { SeedAudienceType } from '@innovationdepartment/proxima-sdk-axios';
import { Form } from '@innovationdepartment/proxima-ui';
import { BRAND_CATEGORY_LABELS } from 'constants/brandCategories';
import { useAudiencesApi, useShowSpinner } from 'hooks';
import { useMemo, useState } from 'react';
import { useAdAccountStore, useBrandStore, useIntegrationsStore } from 'stores';
import type {
  CategoryShoppersProps,
  CreateSeedAudienceSchema,
  CreateSeedAudiencesFormSchema,
  FlavorCategorySchema
} from 'types/audiences';
import { IntegrationType } from 'types/integrations';
import CategoryShoppersView from './CategoryShoppers.View';

const PROHIBITED_WORDS_IN_AUDIENCE_NAME = [
  'health',
  'healthy',
  'sexual',
  'wellness',
  'pregnant',
  'pregnancy',
  'pain'
];

const CategoryShoppers = (props: CategoryShoppersProps) => {
  const { galaxies: allGalaxies, onClose, onSeedAudiencesCreation } = props;
  const { createSeedAudience } = useAudiencesApi();
  const [submitting, setSubmitting] = useState(false);
  const { brand } = useBrandStore();
  const flavorCategory = useMemo<FlavorCategorySchema | undefined>(
    () =>
      brand.category
        ? { id: brand.category, name: BRAND_CATEGORY_LABELS[brand.category] }
        : undefined,
    [brand.category]
  );

  const { getAllIntegrations } = useIntegrationsStore();
  const { metaAdAccountsList } = useAdAccountStore();

  const [adAccount] = metaAdAccountsList;

  useShowSpinner({ show: submitting });

  /* get only active shopify integration */
  const [shopifyIntegration] = getAllIntegrations({ type: IntegrationType.Shopify, active: true });

  const galaxies = allGalaxies.filter(galaxy => !galaxy.isInternal);

  const initialValues: CreateSeedAudiencesFormSchema = {
    galaxies,
    lookalikeSize: '0.1' as unknown as number,
    integrationType: IntegrationType.Facebook,
    adAccountId: adAccount?.adAccountId ?? '',
    names: [], // this is set in CreateSeedAudiencesNames.Container.tsx
    referenceBrandProportionTarget: 25
  };

  const canCreate = initialValues.galaxies.length > 0 && Boolean(adAccount);

  type FormProps = PropsOf<typeof Form<CreateSeedAudiencesFormSchema>>;

  const onSubmit: FormProps['onSubmit'] = async (formData, formApi) => {
    if (!canCreate) return;
    if (!adAccount) return;

    let referenceBrandTargetPercentage: string | number | undefined =
      formData.referenceBrandProportionTarget ?? '25';

    if (!shopifyIntegration) {
      /* if not connected to shopify, remove target percentage value */
      referenceBrandTargetPercentage = undefined;
    } else {
      /* convert to percentage */
      referenceBrandTargetPercentage = +referenceBrandTargetPercentage / 100;
    }

    setSubmitting(true);
    const data: CreateSeedAudienceSchema = {
      galaxyIds: formData.galaxies.map(galaxy => galaxy.id),
      integrations: [
        {
          adAccountId: formData.adAccountId,
          integrationType: formData.integrationType,
          size:
            formData.lookalikeSize ??
            0.01 /* unlikely to get here without validation but TS complains */
        }
      ],
      seedAudienceType: SeedAudienceType.SelfService,
      referenceBrandProportionTarget: referenceBrandTargetPercentage,
      names: formData.names
    };

    await createSeedAudience(data);

    onSeedAudiencesCreation();
    onClose();

    setSubmitting(false);

    /* reset form state after creation */
    formApi.reset();
  };

  const validate: FormProps['validate'] = formData => {
    type ValidationErrorValue = string | string[] | { name: string }[];
    type ValidationError = { [key in keyof typeof initialValues]?: ValidationErrorValue };
    const errors: ValidationError = {};

    const { referenceBrandProportionTarget } = formData;

    const proportionTargetString = +(referenceBrandProportionTarget ?? '');

    const proportionTargetOutOfValidRange =
      proportionTargetString > 100 || proportionTargetString < 0;

    if (proportionTargetOutOfValidRange)
      errors.referenceBrandProportionTarget = 'Must be between 0-100%';
    /* validate names of seed audiences */
    formData.names?.forEach((name, index) => {
      const setError = (error: string) => {
        if (!errors.names) errors.names = [];
        /* lets set the errors in the same format as the form data */
        (errors.names as { name: string }[])[index] = { name: error };
      };

      if (!name.name) {
        setError('Please enter a valid name.');
      } else if (name.name.length > 100) {
        setError('Seed audience name is too long (max 100 chars)');
      } else if (
        // Do not include prohibited words in the audience name
        PROHIBITED_WORDS_IN_AUDIENCE_NAME.some(word => name.name.toLowerCase().includes(word))
      ) {
        setError(
          `Please avoid using words like ${PROHIBITED_WORDS_IN_AUDIENCE_NAME.join(', ')} in the audience name.`
        );
      }
    });

    return errors;
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
      <CategoryShoppersView
        {...props}
        galaxies={galaxies}
        canCreate={canCreate}
        loading={false}
        submitting={submitting}
        flavorCategory={flavorCategory}
      />
    </Form>
  );
};

export default CategoryShoppers;
